import { useState, useEffect } from "react";
import { Database, RotateCcw, CheckCircle } from "lucide-react";
import styles from "./PricingLogic.module.css";
import { useToast } from "../../../context/ToastContext";
import {
  usePricingRules,
  useUpdatePricingRule,
  useRoundingRules,
  useUpdateRoundingRule,
  usePricingValidation,
  useUpdatePricingValidation,
  RULE_NAME_TO_SOURCE,
  SOURCE_TO_RULE_NAME,
  RULE_NAME_TO_STRATEGY,
  STRATEGY_TO_RULE_NAME,
} from "../../../api/pricing/usePricingLogicApi";

// ── Tabs ───────────────────────────────────────────────────
const TABS = [
  { key: "overview",   label: "Overview" },
  { key: "base",       label: "Base Price Calculation", icon: <Database size={13} /> },
  { key: "rounding",   label: "Rounding Rules",         icon: <RotateCcw size={13} /> },
  { key: "validity",   label: "Validity & Activation",  icon: <CheckCircle size={13} /> },
];

const SOURCE_OPTIONS = [
  { key: "supplierBuyPrice", label: "Supplier Buy Price", desc: "Use the negotiated buy price from the supplier contract." },
  { key: "apiPricing",       label: "API Pricing",        desc: "Use the live price returned directly from the supplier API." },
  { key: "offlineInput",     label: "Offline Input",      desc: "Manually entered price for offline or non-API bookings." },
];

const STRATEGY_OPTIONS = [
  { value: "round",  label: "Standard Round",     desc: "Round to nearest (0.5 rounds up)" },
  { value: "ceil",   label: "Always Round Up",   desc: "Always round up to next unit" },
  { value: "floor",  label: "Always Round Down", desc: "Always round down to next unit" },
  { value: "banker", label: "Banker's Rounding",  desc: "Round half to even (financial standard)" },
];

// ── Overview Tab ───────────────────────────────────────────
const OverviewTab = ({ config, onGoTo }) => (
  <div className={styles.overviewGrid}>
    <div className={styles.overviewCard} onClick={() => onGoTo("base")}>
      <div className={styles.overviewCardIcon}><Database size={20} /></div>
      <div>
        <h4>Base Price Calculation</h4>
        <p>Configure which price sources are used: supplier buy price, API pricing, or offline input.</p>
        <div className={styles.overviewMeta}>
          <span className={`${styles.status} ${config.base.activeSource ? styles.success : styles.inactive}`}>
            {SOURCE_OPTIONS.find((o) => o.key === config.base.activeSource)?.label || "Not configured"}
          </span>
        </div>
      </div>
    </div>

    <div className={styles.overviewCard} onClick={() => onGoTo("rounding")}>
      <div className={styles.overviewCardIcon}><RotateCcw size={20} /></div>
      <div>
        <h4>Rounding Rules</h4>
        <p>Define how prices are rounded — strategy, decimal places, and currency dependency.</p>
        <div className={styles.overviewMeta}>
          <span className={`${styles.status} ${styles.success}`}>
            {STRATEGY_OPTIONS.find((o) => o.value === config.rounding.strategy)?.label || config.rounding.strategy} · {config.rounding.decimals} decimals
          </span>
        </div>
      </div>
    </div>

    <div className={styles.overviewCard} onClick={() => onGoTo("validity")}>
      <div className={styles.overviewCardIcon}><CheckCircle size={20} /></div>
      <div>
        <h4>Validity & Activation</h4>
        <p>Control the active status and validity period of the pricing logic configuration.</p>
        <div className={styles.overviewMeta}>
          <span className={`${styles.status} ${config.validity.active ? styles.success : styles.inactive}`}>
            {config.validity.active ? "Active" : "Inactive"}
          </span>
          {config.validity.startDate && (
            <span className={styles.dateRange}>
              {config.validity.startDate} → {config.validity.endDate || "No end"}
            </span>
          )}
        </div>
      </div>
    </div>
  </div>
);

// ── Base Price Calculation Tab ─────────────────────────────
const BasePriceTab = ({ rules, loading, activeSource, refetch }) => {
  const { showToast } = useToast();
  const { execute: updateRule, loading: saveLoading } = useUpdatePricingRule();
  const [selectedSource, setSelectedSource] = useState(activeSource);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    setSelectedSource(activeSource);
  }, [activeSource]);

  const handleSave = async () => {
    const ruleName = SOURCE_TO_RULE_NAME[selectedSource];
    const rule = rules?.find((r) => r.rule_name === ruleName);
    if (!rule) {
      showToast("Rule not found", "error");
      return;
    }
    setIsSaving(true);
    try {
      await updateRule(rule.id, true);
      await refetch();
      showToast("Base price configuration saved", "success");
    } catch {
      showToast("Failed to save configuration", "error");
    } finally {
      setIsSaving(false);
    }
  };

  const saveDisabled = loading || saveLoading || isSaving;

  return (
    <div className={styles.configSection}>
      <div className={styles.toolbar}>
        <div className={styles.manager}>Base Price Calculation</div>
      </div>

      <div className={styles.warningBar}>
        <span className={styles.warningIcon}>⚠</span>
        <span>This determines the source price used before any markup is applied. Exactly one primary source must be active.</span>
      </div>

      <div className={styles.tableSection}>
        <div className={styles.configBlock}>
          <h4 className={styles.configTitle}>Price Source Configuration</h4>
          <p className={styles.configDesc}>Select which input sources are used for base price calculation.</p>

          <div className={styles.sourceOptions}>
            {SOURCE_OPTIONS.map((src) => (
              <div
                key={src.key}
                className={`${styles.sourceOption} ${selectedSource === src.key ? styles.sourceOptionActive : ""}`}
                onClick={() => setSelectedSource(src.key)}
              >
                <div className={styles.radioGroup}>
                  <label className={styles.radioItem}>
                    <input
                      type="radio"
                      name="priceSource"
                      checked={selectedSource === src.key}
                      onChange={() => setSelectedSource(src.key)}
                    />
                  </label>
                </div>
                <div>
                  <strong>{src.label}</strong>
                  <p>{src.desc}</p>
                </div>
              </div>
            ))}
          </div>

          <div className={styles.configFooter}>
            <button
              className={styles.saveChangesBtn}
              onClick={handleSave}
              disabled={saveDisabled}
            >
              {saveDisabled ? "Saving…" : "Save Configuration"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ── Rounding Rules Tab ─────────────────────────────────────
const RoundingTab = ({ rules, loading, activeStrategy, refetch }) => {
  const { showToast } = useToast();
  const { execute: updateRule, loading: saveLoading } = useUpdateRoundingRule();
  const [selectedStrategy, setSelectedStrategy] = useState(activeStrategy);
  const [decimals, setDecimals] = useState(2);
  const [currencyDependent, setCurrencyDependent] = useState("global");

  useEffect(() => {
    setSelectedStrategy(activeStrategy);
  }, [activeStrategy]);

  const handleSave = async () => {
    try {
      const ruleName = STRATEGY_TO_RULE_NAME[selectedStrategy];
      const rule = rules?.find((r) => r.rule_name === ruleName);
      if (!rule) {
        showToast("Rule not found", "error");
        return;
      }
      await updateRule(rule.id, true);
      await refetch();
      showToast("Rounding rules saved", "success");
    } catch {
      showToast("Failed to save rounding rules", "error");
    }
  };

  return (
    <div className={styles.configSection}>
      <div className={styles.toolbar}>
        <div className={styles.manager}>Rounding Rules</div>
      </div>

      <div className={styles.tableSection}>
        <div className={styles.configBlock}>
          <h4 className={styles.configTitle}>Rounding Strategy</h4>
          <p className={styles.configDesc}>Define how calculated prices are rounded before display and invoicing.</p>

          {/* Strategy */}
          <div className={styles.fieldGroup}>
            <label>Rounding Strategy</label>
            <div className={styles.optionCards}>
              {STRATEGY_OPTIONS.map((opt) => (
                <div
                  key={opt.value}
                  className={`${styles.optionCard} ${selectedStrategy === opt.value ? styles.optionCardActive : ""}`}
                  onClick={() => setSelectedStrategy(opt.value)}
                >
                  <div className={styles.radioGroup}>
                    <label className={styles.radioItem}>
                      <input
                        type="radio"
                        name="roundStrategy"
                        checked={selectedStrategy === opt.value}
                        onChange={() => setSelectedStrategy(opt.value)}
                      />
                    </label>
                  </div>
                  <div>
                    <strong>{opt.label}</strong>
                    <span>{opt.desc}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Decimals */}
          <div className={styles.formGrid}>
            <div className={styles.field}>
              <label>Decimal Places</label>
              <select value={decimals} onChange={(e) => setDecimals(Number(e.target.value))}>
                <option value={0}>0 — Whole numbers only</option>
                <option value={1}>1 — e.g. 12.5</option>
                <option value={2}>2 — e.g. 12.50 (standard)</option>
                <option value={3}>3 — e.g. 12.500</option>
              </select>
            </div>

            <div className={styles.field}>
              <label>Currency Dependency</label>
              <select value={currencyDependent} onChange={(e) => setCurrencyDependent(e.target.value)}>
                <option value="global">Global (same rule for all currencies)</option>
                <option value="per-currency">Per Currency (custom per currency)</option>
              </select>
            </div>
          </div>

          {/* Preview */}
          <div className={styles.previewBox}>
            <span className={styles.previewLabel}>Preview</span>
            <span className={styles.previewValue}>
              {(123.456789).toLocaleString("en-EU", {
                minimumFractionDigits: decimals,
                maximumFractionDigits: decimals,
              })}
            </span>
            <span className={styles.previewNote}>based on 123.456789 with {decimals} decimal(s), {selectedStrategy} strategy</span>
          </div>

          <div className={styles.configFooter}>
            <button
              className={styles.saveChangesBtn}
              onClick={handleSave}
              disabled={loading || saveLoading}
            >
              {saveLoading ? "Saving…" : "Save Rounding Rules"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ── Validity Tab ───────────────────────────────────────────
const ValidityTab = ({ config, loading, refetch, hasValidationRecord }) => {
  const { showToast } = useToast();
  const { execute: updateValidation, loading: saveLoading } = useUpdatePricingValidation();
  const [form, setForm] = useState(config);
  const [isSaving, setIsSaving] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    setForm(config);
  }, [config]);

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await updateValidation({
        status: form.active,
        start_date: form.startDate || null,
        end_date: form.endDate || null,
      });
      await refetch();
      showToast("Validity settings saved", "success");
    } catch (err) {
      const msg = err.response?.data?.message || err.response?.data?.errors?.[0] || "Failed to save validity settings";
      showToast(msg, "error");
    } finally {
      setIsSaving(false);
    }
  };

  const saveDisabled = loading || saveLoading || isSaving || !hasValidationRecord;

  return (
    <div className={styles.configSection}>
      <div className={styles.toolbar}>
        <div className={styles.manager}>Validity & Activation</div>
      </div>

      {!hasValidationRecord && !loading && (
        <div className={styles.warningBar}>
          <span className={styles.warningIcon}>⚠</span>
          <span>Configure Base Price Calculation and Rounding Rules first to enable validity settings.</span>
        </div>
      )}

      <div className={styles.tableSection}>
        <div className={styles.configBlock}>
          <h4 className={styles.configTitle}>Pricing Logic Status</h4>
          <p className={styles.configDesc}>Control when this pricing logic configuration is active and applied.</p>

          <div className={styles.formGrid}>
            <div className={styles.field}>
              <label>Start Date</label>
              <input type="date" value={form.startDate || ""} onChange={(e) => set("startDate", e.target.value)} />
            </div>
            <div className={styles.field}>
              <label>End Date</label>
              <input type="date" value={form.endDate || ""} onChange={(e) => set("endDate", e.target.value)} />
            </div>
          </div>

          <div className={styles.toggleField}>
            <div>
              <strong>Active Status</strong>
              <p>When inactive, the pricing logic will not be applied to any bookings.</p>
            </div>
            <div className={styles.toggleWrapper}>
              <span className={form.active ? styles.toggleLabelOn : styles.toggleLabelOff}>
                {form.active ? "Active" : "Inactive"}
              </span>
              <div
                className={`${styles.toggle} ${form.active ? styles.on : ""}`}
                onClick={() => set("active", !form.active)}
              >
                <div className={styles.knob} />
              </div>
            </div>
          </div>

          <div className={styles.configFooter}>
            <button
              className={styles.saveChangesBtn}
              onClick={handleSave}
              disabled={saveDisabled}
            >
              {saveDisabled ? "Saving…" : "Save Validity"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ── Main Page ──────────────────────────────────────────────
const PricingLogic = () => {
  const [activeTab, setActiveTab] = useState("overview");

  const { data: pricingRules, loading: pricingLoading, refetch: refetchPricing } = usePricingRules();
  const { data: roundingRules, loading: roundingLoading, refetch: refetchRounding } = useRoundingRules();
  const { data: validationData, loading: validationLoading, refetch: refetchValidation } = usePricingValidation();

  const activeSource = (() => {
    const active = pricingRules?.find((r) => r.status);
    return active ? (RULE_NAME_TO_SOURCE[active.rule_name] ?? "supplierBuyPrice") : "supplierBuyPrice";
  })();

  const activeStrategy = (() => {
    const active = roundingRules?.find((r) => r.status);
    return active ? (RULE_NAME_TO_STRATEGY[active.rule_name] ?? "round") : "round";
  })();

  const validityConfig = {
    active: validationData?.status ?? false,
    startDate: validationData?.start_date || "",
    endDate: validationData?.end_date || "",
  };

  const config = {
    base: { activeSource },
    rounding: { strategy: activeStrategy, decimals: 2, currencyDependent: "global" },
    validity: validityConfig,
  };

  return (
    <div className={styles.container}>
      <div className={styles.toolbar}>
        <div className={styles.manager}>Pricing Logic</div>
      </div>

      <div className={styles.warningBar}>
        <span className={styles.warningIcon}>⚠</span>
        <span>Pricing logic settings affect how all selling prices are built. Changes apply to both online and offline bookings.</span>
      </div>

      {/* Stats */}
      <div className={styles.statsRow}>
        <div className={styles.statCard}>
          <span className={`${styles.statNum} ${validityConfig.active ? styles.statGreen : styles.statGray}`}>
            {validityConfig.active ? "Active" : "Inactive"}
          </span>
          <span className={styles.statLabel}>Logic Status</span>
        </div>
        <div className={styles.statCard}>
          <span className={styles.statNum}>
            {activeSource === "supplierBuyPrice" ? "Supplier" : activeSource === "apiPricing" ? "API" : "Offline"}
          </span>
          <span className={styles.statLabel}>Price Source</span>
        </div>
        <div className={styles.statCard}>
          <span className={styles.statNum}>2</span>
          <span className={styles.statLabel}>Decimal Places</span>
        </div>
        <div className={styles.statCard}>
          <span className={styles.statNum} style={{ textTransform: "capitalize" }}>{activeStrategy}</span>
          <span className={styles.statLabel}>Rounding Strategy</span>
        </div>
      </div>

      {/* Tabs */}
      <div className={styles.tabs}>
        {TABS.map((tab) => (
          <button
            key={tab.key}
            className={`${styles.tab} ${activeTab === tab.key ? styles.tabActive : ""}`}
            onClick={() => setActiveTab(tab.key)}
          >
            {tab.icon && tab.icon}
            {tab.label}
          </button>
        ))}
      </div>

      {activeTab === "overview" && (
        <OverviewTab config={config} onGoTo={setActiveTab} />
      )}
      {activeTab === "base" && (
        <BasePriceTab
          rules={pricingRules}
          loading={pricingLoading}
          activeSource={activeSource}
          refetch={async () => {
            await refetchPricing();
            await refetchValidation();
          }}
        />
      )}
      {activeTab === "rounding" && (
        <RoundingTab
          rules={roundingRules}
          loading={roundingLoading}
          activeStrategy={activeStrategy}
          refetch={async () => {
            await refetchRounding();
            await refetchValidation();
          }}
        />
      )}
      {activeTab === "validity" && (
        <ValidityTab
          config={validityConfig}
          loading={validationLoading}
          refetch={refetchValidation}
          hasValidationRecord={!!validationData?.pricing_rule_data}
        />
      )}
    </div>
  );
};

export default PricingLogic;